/**
 * In welcher Reihenfolge Freigaben stehen: **wer zuletzt gelesen hat, oben**.
 *
 * Florian, 20.9.2026, unterwegs: „Diejenigen, die vor kurzer Zeit gelesen
 * haben, oben und diejenigen, die vor längerer Zeit gelesen haben, unten.“
 * Die Liste stand bis dahin in der Reihenfolge des Anlegens — richtig für
 * jemanden, der Links verwaltet, und falsch für jemanden, der unterwegs
 * wissen will, wer gerade mitliest. Das Zweite ist die Frage, mit der man
 * die Seite öffnet.
 *
 * ## Die Regel
 *
 * 1. Gelesene vor nie gelesenen, die gelesenen nach `last_seen_at` absteigend.
 * 2. Nie gelesene nach Anlagedatum, die jüngste zuerst — ein Link von eben
 *    ist der, auf dessen ersten Aufruf man wartet.
 * 3. Abgelaufene ans Ende ihrer Liste: Sie können nicht mehr gelesen werden,
 *    und ihr letzter Aufruf sagt nichts mehr darüber, wer mitliest.
 * 4. Die Kennung als letzter Schlüssel, damit die Liste bei jedem Laden
 *    gleich aussieht.
 *
 * Verglichen wird als Text: Der Server liefert beide Zeitstempel als UTC in
 * derselben ISO-Form, und die sortiert sich selbst. Gerechnet wird nur für
 * „abgelaufen“ — und dort mit angehängtem `Z`, weil die Angabe sonst als
 * Ortszeit gelesen würde (dieselbe Falle wie in `datum.zeitpunktHier`).
 */
export interface Folgezeile {
  id: number;
  last_seen_at?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
}

/** `true`, wenn die Frist der Freigabe verstrichen ist. */
export function istAbgelaufen(f: Folgezeile, jetzt: number = Date.now()): boolean {
  if (!f.expires_at) return false;
  const roh = f.expires_at.replace(' ', 'T');
  const t = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(roh) ? roh : `${roh}Z`).getTime();
  return !Number.isNaN(t) && t < jetzt;
}

/** Zwei Zeitstempel absteigend, fehlende ans Ende. */
export function zuletztZuerst(a: string | null | undefined, b: string | null | undefined): number {
  if (a && b) return a < b ? 1 : a > b ? -1 : 0;
  if (a) return -1;
  if (b) return 1;
  return 0;
}

export function nachZuletztGelesen<T extends Folgezeile>(
  liste: readonly T[],
  jetzt: number = Date.now()
): T[] {
  return [...liste].sort(
    (a, b) =>
      Number(istAbgelaufen(a, jetzt)) - Number(istAbgelaufen(b, jetzt)) ||
      zuletztZuerst(a.last_seen_at, b.last_seen_at) ||
      zuletztZuerst(a.created_at, b.created_at) ||
      a.id - b.id
  );
}
